// operationsApi.js
//
// API service layer for the Operations Dashboard feature.
// Deliberately thin: it reuses your EXISTING shared axios instance (`api`)
// from api.js, so it automatically gets:
//   - the "Authorization: Token <token>" header via your existing request interceptor
//   - global 401 handling (clear storage + redirect to /login) via your existing
//     response interceptor
// No auth logic is duplicated here.
//
// ⚠️ IMPORTANT: adjust the import path below to match where your api.js
// (the file exporting the shared axios instance as `export default api;`)
// actually lives in your project, e.g.:
//   import api from '../api';            // if src/api.js
//   import api from '../utils/api';      // if src/utils/api.js
//   import api from '../services/api';   // if src/services/api.js
import api from '../../api';

/**
 * GET /api/clients/operations/dashboard/?date=YYYY-MM-DD
 * @param {string} date - YYYY-MM-DD
 */
export const getOperationsDashboard = (date) =>
  api.get('operations/dashboard/', { params: { date } }).then((res) => res.data);

/**
 * GET /api/clients/operations/camps/<camp_id>/
 * @param {string|number} campId
 */
export const getOperationsCampDetail = (campId) =>
  api.get(`operations/camps/${campId}/`).then((res) => res.data);

/**
 * GET /api/clients/operations/camps/<camp_id>/cases/
 * @param {string|number} campId
 */
export const getOperationsCampCases = (campId) =>
  api.get(`operations/camps/${campId}/cases/`).then((res) => res.data);


/**
 * GET /api/clients/operations/camps/<camp_id>/services/
 * @param {string|number} campId
 */
export const getOperationsCampServices = (campId) =>
  api
    .get(`operations/camps/${campId}/services/`)
    .then((res) => res.data);

/**
 * GET /api/clients/operations/camps/<camp_id>/technicians/
 * @param {string|number} campId
 */
export const getOperationsCampTechnicians = (campId) =>
  api.get(`operations/camps/${campId}/technicians/`).then((res) => res.data);

/**
 * GET /api/clients/operations/camps/<camp_id>/financial-target/
 *
 * Returns the camp's financial target: contract_value, cost_target_type,
 * cost_target_percentage, cost_target_amount, calculated_target_amount,
 * actual_cost, remaining_target, target_utilization_percentage, and
 * target_status.
 *
 * Only called when getOperationsCampDetail's response does NOT already
 * include a `financial_target` field — OperationsCampDetails.jsx prefers
 * the embedded field first to avoid a redundant request.
 *
 * @param {string|number} campId
 */
export const getOperationsCampFinancialTarget = (campId) =>
  api
    .get(`camps/${campId}/financial-target/`)
    .then((res) => res.data);

/**
 * PATCH /api/clients/operations/camps/<camp_id>/financial-target/
 *
 * Operations users may ONLY ever send `contract_value` here. Target type,
 * target percentage, and target amount are Admin-only fields and must
 * never be sent from the Operations frontend.
 *
 * @param {string|number} campId
 * @param {{ contract_value: number }} data
 */
export const updateOperationsCampFinancialTarget = (campId, data) =>
  api
    .patch(`camps/${campId}/financial-target/`, data)
    .then((res) => res.data);

export const operationsApi = {
  getDashboard: getOperationsDashboard,
  getCampDetail: getOperationsCampDetail,
  getCampCases: getOperationsCampCases,
  getCampTechnicians: getOperationsCampTechnicians,
  getCampServices: getOperationsCampServices,
  getCampFinancialTarget: getOperationsCampFinancialTarget,
  updateCampFinancialTarget: updateOperationsCampFinancialTarget,
};

export default operationsApi;